/**
 * useAppMessages Hook
 *
 * React Query hook for fetching and managing application messages.
 * Supports unread count, marking as read, and archiving.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { appMessageService } from '../../../api/app-message.service';
import type {
  AppMessageSanitized,
  AppMessageListQuery,
  AppMessageView,
  MessagePriority,
} from '../../../types/app-message.types';
import { getErrorMessage } from '../../../lib/utils/errors';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const APP_MESSAGES_QUERY_KEY = 'appMessages';
export const APP_MESSAGES_UNREAD_COUNT_QUERY_KEY = 'appMessagesUnreadCount';

// ============================================================================
// HOOK OPTIONS
// ============================================================================

export interface UseAppMessagesOptions {
  /** Filter by specific application */
  applicationId?: string;
  /** Show only unread messages */
  unreadOnly?: boolean;
  /** Enable/disable the query */
  enabled?: boolean;
  /** Filter by message type */
  type?: string;
  /** Pagination limit */
  limit?: number;
  /** View type (inbox, archived, deleted) - defaults to inbox */
  view?: AppMessageView;
  /** Filter by priority */
  priority?: MessagePriority;
  /** Filter by date range start */
  dateStart?: string;
  /** Filter by date range end */
  dateEnd?: string;
}

// ============================================================================
// HOOK RESULT
// ============================================================================

export interface UseAppMessagesResult {
  /** List of app messages */
  messages: AppMessageSanitized[];
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Unread count */
  unreadCount: number;
  /** Unread count loading state */
  isLoadingUnreadCount: boolean;
  /** Has more pages */
  hasMore: boolean;
  /** Pagination cursor */
  cursor?: string;
  /** Refresh messages */
  refresh: () => void;
  /** Mark message as read */
  markAsRead: (messageId: string) => Promise<void>;
  /** Mark all messages as read */
  markAllAsRead: () => Promise<void>;
  /** Archive a message */
  archiveMessage: (messageId: string) => Promise<void>;
  /** Unarchive a message */
  unarchiveMessage: (messageId: string) => Promise<void>;
  /** Soft delete a message */
  softDeleteMessage: (messageId: string) => Promise<void>;
  /** Restore a deleted message */
  restoreMessage: (messageId: string) => Promise<void>;
  /** Permanently delete a message */
  permanentDeleteMessage: (messageId: string) => Promise<void>;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Hook for fetching and managing app messages
 */
export function useAppMessages(options?: UseAppMessagesOptions): UseAppMessagesResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const tenantId = user?.tenantId || '';
  const userId = user?.userId || '';
  const applicationId = options?.applicationId || '';
  const view = options?.view || 'inbox';

  const enabled = options?.enabled !== false && !!tenantId && !!userId && !!applicationId;

  // Build query key that includes all filter options
  const queryKeyParams = [
    APP_MESSAGES_QUERY_KEY,
    tenantId,
    applicationId,
    userId,
    view,
    options?.unreadOnly,
    options?.type,
    options?.priority,
    options?.dateStart,
    options?.dateEnd,
    options?.limit,
  ];

  // ============================================================================
  // QUERIES
  // ============================================================================

  /**
   * Fetch messages query
   */
  const {
    data: messagesData,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery<{ messages: AppMessageSanitized[]; cursor?: string }, Error>({
    queryKey: queryKeyParams,
    queryFn: async () => {
      const query: AppMessageListQuery = {
        view,
        unreadOnly: options?.unreadOnly,
        type: options?.type,
        priority: options?.priority,
        dateStart: options?.dateStart,
        dateEnd: options?.dateEnd,
        limit: options?.limit || 20,
      };
      return await appMessageService.listMessages(tenantId, applicationId, userId, query);
    },
    enabled,
    staleTime: 15000, // 15 seconds
    refetchInterval: (query) => {
      if (query.state.error && (query.state.error as { response?: { status?: number } }).response?.status === 401) return false;
      return 60000;
    },
  });

  /**
   * Fetch unread count query
   */
  const { data: unreadCount = 0, isLoading: isLoadingUnreadCount } = useQuery<number, Error>({
    queryKey: [APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, applicationId, userId],
    queryFn: () => appMessageService.getUnreadCount(tenantId, applicationId, userId),
    enabled,
    staleTime: 15000, // 15 seconds
    refetchInterval: (query) => {
      if (query.state.error && (query.state.error as { response?: { status?: number } }).response?.status === 401) return false;
      return 60000;
    },
  });

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  /**
   * Mark as read mutation
   */
  const markAsReadMutation = useMutation({
    mutationFn: (messageId: string) =>
      appMessageService.markAsRead(tenantId, applicationId, userId, messageId),
    onSuccess: (updatedMessage) => {
      // Update the messages cache
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((msg) =>
              msg.messageId === updatedMessage.messageId ? updatedMessage : msg
            ),
          };
        }
      );
      // Invalidate unread count
      queryClient.invalidateQueries({
        queryKey: [APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, applicationId, userId],
      });
    },
  });

  /**
   * Mark all as read mutation
   */
  const markAllAsReadMutation = useMutation({
    mutationFn: () => appMessageService.markAllAsRead(tenantId, applicationId, userId),
    onSuccess: () => {
      // Update all messages to read in cache
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((msg) => ({ ...msg, isRead: true })),
          };
        }
      );
      // Invalidate unread count
      queryClient.invalidateQueries({
        queryKey: [APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, applicationId, userId],
      });
    },
  });

  /**
   * Archive message mutation
   */
  const archiveMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      appMessageService.archiveMessage(tenantId, applicationId, userId, messageId),
    onSuccess: (archivedMessage) => {
      // Remove from messages cache (it moves to archived view)
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.filter((msg) => msg.messageId !== archivedMessage.messageId),
          };
        }
      );

      // Invalidate all app messages queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [APP_MESSAGES_QUERY_KEY, tenantId, applicationId, userId],
        exact: false,
      });

      // Invalidate unread count if message was unread
      if (!archivedMessage.isRead) {
        queryClient.invalidateQueries({
          queryKey: [APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, applicationId, userId],
        });
      }
    },
  });

  /**
   * Unarchive message mutation
   */
  const unarchiveMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      appMessageService.unarchiveMessage(tenantId, applicationId, userId, messageId),
    onSuccess: (_, messageId) => {
      // Remove from current view (archived) as it moves back to inbox
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.filter((msg) => msg.messageId !== messageId),
          };
        }
      );

      // Invalidate all app messages queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [APP_MESSAGES_QUERY_KEY, tenantId, applicationId, userId],
        exact: false,
      });
    },
  });

  /**
   * Soft delete message mutation
   */
  const softDeleteMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      appMessageService.softDeleteMessage(tenantId, applicationId, userId, messageId),
    onSuccess: (deletedMessage, messageId) => {
      // Remove from current view as it moves to deleted view
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.filter((msg) => msg.messageId !== messageId),
          };
        }
      );

      // Invalidate all app messages queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [APP_MESSAGES_QUERY_KEY, tenantId, applicationId, userId],
        exact: false,
      });

      // Invalidate unread count if message was unread
      if (!deletedMessage.isRead) {
        queryClient.invalidateQueries({
          queryKey: [APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, applicationId, userId],
        });
      }
    },
  });

  /**
   * Restore message mutation
   */
  const restoreMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      appMessageService.restoreMessage(tenantId, applicationId, userId, messageId),
    onSuccess: (_, messageId) => {
      // Remove from current view (deleted) as it moves back to inbox
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.filter((msg) => msg.messageId !== messageId),
          };
        }
      );

      // Invalidate all app messages queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [APP_MESSAGES_QUERY_KEY, tenantId, applicationId, userId],
        exact: false,
      });
    },
  });

  /**
   * Permanent delete message mutation
   */
  const permanentDeleteMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      appMessageService.permanentDeleteMessage(tenantId, applicationId, userId, messageId),
    onSuccess: (_, messageId) => {
      // Remove from current view permanently
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.filter((msg) => msg.messageId !== messageId),
          };
        }
      );
    },
  });

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    messages: messagesData?.messages || [],
    isLoading,
    error: queryError ? getErrorMessage(queryError) : null,
    unreadCount,
    isLoadingUnreadCount,
    hasMore: !!messagesData?.cursor,
    cursor: messagesData?.cursor,
    refresh: () => {
      refetch();
      queryClient.invalidateQueries({
        queryKey: [APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, applicationId, userId],
      });
    },
    markAsRead: async (messageId: string) => {
      await markAsReadMutation.mutateAsync(messageId);
    },
    markAllAsRead: async () => {
      await markAllAsReadMutation.mutateAsync();
    },
    archiveMessage: async (messageId: string) => {
      await archiveMessageMutation.mutateAsync(messageId);
    },
    unarchiveMessage: async (messageId: string) => {
      await unarchiveMessageMutation.mutateAsync(messageId);
    },
    softDeleteMessage: async (messageId: string) => {
      await softDeleteMessageMutation.mutateAsync(messageId);
    },
    restoreMessage: async (messageId: string) => {
      await restoreMessageMutation.mutateAsync(messageId);
    },
    permanentDeleteMessage: async (messageId: string) => {
      await permanentDeleteMessageMutation.mutateAsync(messageId);
    },
  };
}
